import { useEffect, useState, useRef, useMemo } from 'react';
import { X, Search, Send, Paperclip, FileText, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { usersAPI, messagesAPI, uploadsAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../../context/authStore';
import toast from 'react-hot-toast';

export default function NewMessageModal({ isOpen, onClose, projectId }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sendError, setSendError] = useState('');
  const fileInputRef = useRef(null);
  const timerRef = useRef(null);
  const recipientScopeHint = useMemo(() => {
    if (user?.role === 'facility_worker') {
      return 'Search eligible internal users only: your facility admins/managers, plus Stratel internal team.';
    }
    if (['facility_admin', 'facility_manager'].includes(user?.role)) {
      return 'Search users in your organization only.';
    }
    return 'Search internal platform users only.';
  }, [user?.role]);

  useEffect(() => {
    if (!isOpen) return;
    setQ('');
    setResults([]);
    setSelected([]);
    setSubject('');
    setContent('');
  }, [isOpen]);

  useEffect(() => {
    // Debounced search
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.length < 1) {
      setResults([]);
      return;
    }

    timerRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await usersAPI.recipients(q);
        setResults(res.data.users || []);
      } catch (err) {
        console.error('Recipient search failed', err);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, [q]);

  const MAX_UPLOAD_SIZE = 50 * 1024 * 1024; // 50MB
  const ALLOWED_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/plain',
    'text/csv',
  ];

  const uploadWithProgress = (uploadUrl, file, onProgress) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        onProgress(percent);
      }
    };
    return new Promise((resolve, reject) => {
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve({ xhr });
        else reject(new Error('Upload failed with status ' + xhr.status));
      };
      xhr.onerror = () => reject(new Error('Upload failed'));
      xhr.send(file);
    });
  }; 

  const handleFiles = async (files) => {
    if (!files || !files.length) return;

    for (const file of Array.from(files)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        toast.error(`${file.name}: File type not allowed`);
        continue;
      }
      if (file.size > MAX_UPLOAD_SIZE) {
        toast.error(`${file.name}: File too large (max 50MB)`);
        continue;
      }

      const id = `${Date.now()}-${Math.random().toString(36).slice(2,8)}`;
      // Add optimistic attachment entry and keep raw file for retry
      setAttachments((a) => [...a, { id, file, fileName: file.name, fileSize: file.size, fileType: file.type, progress: 0, status: 'uploading', key: null, fileUrl: null, xhr: null }]);

      try {
        setUploading(true);
        const res = await uploadsAPI.getPresignedUrl({ fileName: file.name, fileType: file.type });
        const { uploadUrl, key, fileUrl } = res.data;

        // attach key/url immediately
        setAttachments((a) => a.map((att) => (att.id === id ? { ...att, key, fileUrl } : att)));

        const { xhr } = await uploadWithProgress(uploadUrl, file, (percent) => {
          setAttachments((a) => a.map((att) => (att.id === id ? { ...att, progress: percent } : att)));
        });

        // mark uploaded
        setAttachments((a) => a.map((att) => (att.id === id ? { ...att, progress: 100, status: 'uploaded', xhr: null } : att)));
      } catch (err) {
        console.error('Upload failed', err);
        setAttachments((a) => a.map((att) => (att.id === id ? { ...att, status: 'error', xhr: null } : att)));
        toast.error('Upload failed for ' + file.name);
      } finally {
        setUploading(false);
      }
    }
  }; 

  const cancelUpload = (id) => {
    const att = attachments.find(a => a.id === id);
    if (att && att.xhr) {
      try { att.xhr.abort(); } catch (e) { }
    }
    setAttachments(a => a.map(att => att.id === id ? { ...att, status: 'cancelled', progress: 0, xhr: null } : att));
  };

  const retryUpload = async (id) => {
    const att = attachments.find(a => a.id === id);
    if (!att) return;
    setAttachments(a => a.map(x => x.id === id ? { ...x, status: 'uploading', progress: 0 } : x));
    try {
      const res = await uploadsAPI.getPresignedUrl({ fileName: att.fileName, fileType: att.fileType });
      const { uploadUrl, key, fileUrl } = res.data;
      setAttachments(a => a.map(x => x.id === id ? { ...x, key, fileUrl } : x));
      const { xhr } = await uploadWithProgress(uploadUrl, att.file, (percent) => {
        setAttachments(a => a.map(x => x.id === id ? { ...x, progress: percent } : x));
      });
      setAttachments(a => a.map(x => x.id === id ? { ...x, status: 'uploaded', progress: 100, xhr: null } : x));
    } catch (err) {
      console.error('Retry failed', err);
      setAttachments(a => a.map(x => x.id === id ? { ...x, status: 'error', xhr: null } : x));
      toast.error('Retry failed');
    }
  }; 

  const removeAttachment = (id) => setAttachments((a) => a.filter((x) => x.id !== id));

  const deleteAttachment = async (id) => {
    const att = attachments.find(a => a.id === id);
    if (!att) return;
    if (att.key && !window.confirm('Permanently delete this uploaded file?')) return;
    try {
      if (att.key) await uploadsAPI.delete(att.key);
      setAttachments((a) => a.filter((x) => x.id !== id));
      toast.success('File deleted');
    } catch (err) {
      console.error('Delete failed', err);
      toast.error('Failed to delete file');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const completedUploads = attachments.filter(a => a.status === 'uploaded').length;
  const overallProgress = useMemo(() => {
    const uploadAtts = attachments.filter(a => ['uploading','uploaded','error','cancelled'].includes(a.status));
    if (uploadAtts.length === 0) return 100;
    const sum = uploadAtts.reduce((s, a) => s + (a.progress || 0), 0);
    return Math.round(sum / uploadAtts.length);
  }, [attachments]);

  const createMutation = useMutation({
    mutationFn: (payload) => messagesAPI.create(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries(['messages']);
      const threadId = res.data.thread?._id || res.data.thread?._id || res.data?.thread?._id || res.data?._id;
      onClose();
      if (threadId) navigate(`/messages/${threadId}`);
    },
    onError: (err) => {
      console.error('Failed to create message', err);
      // TODO: show toast
    },
  });

  const toggleSelect = (u) => {
    if (selected.some((s) => s._id === u._id)) {
      setSelected((s) => s.filter((x) => x._id !== u._id));
    } else {
      setSelected((s) => [...s, u]);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSendError('');
    if (!content.trim() && attachments.length === 0) return;
    if (selected.length === 0) return;

    // Block send if any uploads are still in progress
    if (attachments.some(a => a.status === 'uploading')) {
      setSendError('Please wait for uploads to finish or cancel them.');
      return;
    }

    // If any uploads failed or were cancelled, prompt user to retry/remove
    if (attachments.some(a => a.status === 'error' || a.status === 'cancelled')) {
      setSendError('One or more file uploads failed or were cancelled. Retry or remove them before sending.');
      return;
    }

    const attachmentsPayload = attachments.filter(a => a.status === 'uploaded').map(a => ({ fileName: a.fileName, fileUrl: a.fileUrl, key: a.key, fileSize: a.fileSize, fileType: a.fileType }));

    const payload = {
      participants: selected.map((s) => s._id),
      subject: subject || 'Message',
      content,
      attachments: attachmentsPayload,
    };

    // If created from a project thread, include projectId
    if (projectId) payload.projectId = projectId;

    createMutation.mutate(payload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[130] flex items-center justify-center sm:p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 flex h-[100dvh] w-full max-w-2xl flex-col overflow-hidden bg-white shadow-lg sm:h-auto sm:max-h-[calc(100dvh-2rem)] sm:rounded-xl">
        <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 sm:px-6 sm:py-4">
          <h3 className="text-lg font-semibold">New Message</h3>
          <button onClick={onClose} className="p-2 text-secondary-500 hover:text-secondary-700">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
          <div>
            <label className="text-sm font-medium text-secondary-700">To</label>
            <div className="mt-2">
              <div className="flex gap-2 flex-wrap">
                {selected.map((s) => (
                  <span key={s._id} className="px-2 py-1 bg-secondary-100 rounded-full text-sm flex items-center gap-2">
                    {s.firstName} {s.lastName}
                    <button type="button" onClick={() => toggleSelect(s)} className="ml-1 text-secondary-400 hover:text-secondary-600">
                      Ã—
                    </button>
                  </span>
                ))}
              </div>
              <div className="relative mt-2">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="Search by name or email"
                />
                <div className="absolute right-2 top-2 text-secondary-400">
                  {loading ? <span className="animate-spin inline-block h-4 w-4 border-t-2 border-b-2 border-primary-600 rounded-full" /> : <Search size={16} />}
                </div>
                <p className="mt-2 text-xs text-secondary-500">{recipientScopeHint}</p>
                {results.length > 0 && (
                  <div className="bg-white border rounded shadow mt-1 max-h-48 overflow-auto">
                    {results.map((r) => (
                      <button
                        key={r._id}
                        type="button"
                        onClick={() => toggleSelect(r)}
                        className="w-full text-left px-3 py-2 hover:bg-secondary-50 flex items-center gap-2"
                      >
                        <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 text-sm">
                          {r.firstName?.[0]}{r.lastName?.[0]}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-secondary-900">{r.firstName} {r.lastName}</div>
                          <div className="text-xs text-secondary-500">{r.email} • {r.role?.replace('_', ' ')}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {!loading && q.trim() && results.length === 0 && (
                  <div className="mt-2 rounded border border-dashed border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-500">
                    No eligible internal recipients found for "{q}".
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-secondary-700">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full border rounded-lg px-3 py-2 mt-2" placeholder="Subject (optional)" />
          </div>

          <div>
            <label className="text-sm font-medium text-secondary-700">Message</label>
            <textarea value={content} onChange={(e) => setContent(e.target.value)} rows={5} className="w-full resize-y border rounded-lg px-3 py-2 mt-2" />
          </div>

          <div>
            <label className="text-sm font-medium text-secondary-700">Attachments</label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input ref={fileInputRef} type="file" multiple onChange={(e) => handleFiles(e.target.files)} className="hidden" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="px-3 py-1.5 border rounded-lg text-sm hover:bg-secondary-50 flex items-center gap-1"><Paperclip size={14}/> Attach files</button>
              <div className="text-sm text-secondary-500">Max 50MB per file • Supported: jpg, png, pdf, docx</div>
            </div>
            {attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {attachments.map((att) => (
                  <div key={att.id} className="flex items-center gap-2 p-2 bg-secondary-50 rounded-lg">
                    <div className="text-secondary-400"><FileText size={16} /></div>
                    <div className="flex-1">
                      <a href={att.fileUrl || '#'} target="_blank" rel="noreferrer" className="text-sm text-primary-600 truncate">{att.fileName}</a>
                      <div className="text-xs text-secondary-400">{formatFileSize(att.fileSize)}</div>
                      {att.status === 'uploading' && (
                        <div className="mt-1">
                          <div className="h-2 bg-secondary-200 rounded overflow-hidden">
                            <div className="h-2 bg-primary-600" style={{ width: `${att.progress}%` }} />
                          </div>
                          <div className="text-xs text-secondary-500 mt-1">{att.progress}%</div>
                        </div>
                      )}
                      {att.status === 'error' && <div className="text-xs text-red-500 mt-1">Upload failed</div>}
                      {att.status === 'cancelled' && <div className="text-xs text-secondary-500 mt-1">Cancelled</div>}
                    </div>

                    {att.status === 'uploading' && (
                      <button type="button" onClick={() => cancelUpload(att.id)} className="text-secondary-600 hover:text-secondary-800">Cancel</button>
                    )}
                    {(att.status === 'error' || att.status === 'cancelled') && (
                      <button type="button" onClick={() => retryUpload(att.id)} className="text-secondary-600 hover:text-secondary-800">Retry</button>
                    )}
                    <button type="button" onClick={() => removeAttachment(att.id)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                  </div>
                ))}

                {attachments.some(a => a.status === 'uploading') && (
                  <div className="w-full sm:ml-4 sm:w-48">
                    <div className="text-xs text-secondary-500 mb-1">Uploading files</div>
                    <div className="w-full h-2 bg-gray-200 rounded overflow-hidden">
                      <div className="bg-primary-600 h-2" style={{ width: `${overallProgress}%` }} />
                    </div>
                    <div className="text-xs text-secondary-400 text-right">{completedUploads}/{attachments.length} • {overallProgress}%</div>
                  </div>
                )}

              </div>
            )}

          {sendError && (
            <div className="p-3 bg-red-50 border border-red-100 rounded text-red-700 text-sm">
              {sendError} <button onClick={() => setSendError('')} className="underline ml-2">Dismiss</button>
            </div>
          )}

          <div className="sticky bottom-0 -mx-4 -mb-4 flex justify-end gap-2 border-t bg-white px-4 py-3 sm:-mx-6 sm:-mb-6 sm:px-6">
            <button type="button" onClick={onClose} className="px-4 py-2 border rounded">Cancel</button>
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded flex items-center gap-2" disabled={createMutation.isLoading || attachments.some(a => a.status === 'uploading') || (!content.trim() && attachments.length === 0)}>
              <Send size={16} />
              Send
            </button>
          </div>
          </div>


        </form>
      </div>
    </div>
  );
}
